/*
  In-card colour swap.
  Tapping a colour swatch on a product card shows that colourway inside the
  same card (photo, price, sizes, badges, links) instead of leaving the grid.
  Tapping the colour that is already shown opens the product.

  The sibling card is fetched with the Section Rendering API
  (/products/{handle}?section_id=wishlist-product-item), so the markup always
  comes from snippets/product-item.liquid. The swatch row itself is kept, because
  sibling swatches are built from the collection and the section has none.
*/
(function () {
  var SWATCH = '.product-item .product-item-swatch[href]';
  var cache = new Map();
  // Mobile only (same breakpoint as the theme's drawer menu); desktop keeps plain links.
  var mobile = window.matchMedia('(max-width: 959px)');

  function cardUrl(href) {
    var url = new URL(href, window.location.origin);
    return url.pathname + '?section_id=wishlist-product-item';
  }

  function fetchCard(href) {
    var key = cardUrl(href);
    if (!cache.has(key)) {
      var request = fetch(key, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('card ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var card = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-item');
          if (!card) throw new Error('card missing');
          return card;
        })
        .catch(function (err) {
          cache.delete(key);
          throw err;
        });
      cache.set(key, request);
    }
    return cache.get(key);
  }

  function preloadImage(card) {
    var img = card.querySelector('.product-item__image img');
    if (!img) return Promise.resolve();
    var probe = new Image();
    if (img.getAttribute('sizes')) probe.sizes = img.getAttribute('sizes');
    if (img.getAttribute('srcset')) probe.srcset = img.getAttribute('srcset');
    probe.src = img.getAttribute('src');
    var decoded = (probe.decode ? probe.decode() : Promise.resolve()).catch(function () {});
    // Never hold the swap hostage to a slow or stalled decode.
    return Promise.race([decoded, wait(700)]);
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function syncAttributes(target, source) {
    if (!target || !source) return;
    Array.prototype.forEach.call(source.attributes, function (attr) {
      if (attr.name.indexOf('data-') === 0 || attr.name === 'href' || attr.name === 'value' || attr.name === 'aria-label') {
        target.setAttribute(attr.name, attr.value);
      }
    });
  }

  function replace(oldCard, newCard, selector) {
    var current = oldCard.querySelector(selector);
    var next = newCard.querySelector(selector);
    if (current && next) current.replaceWith(next);
    else if (current && !next) current.remove();
  }

  // Cards inside a collection link to /collections/<handle>/products/<product>
  // so the product page knows where the shopper came from (breadcrumbs). The
  // fetched sibling card has no collection, so its links get the same prefix.
  function collectionPrefix(item) {
    var link = item.querySelector('.product-item__image-link');
    var match = link && (link.getAttribute('href') || '').match(/^(\/collections\/[^/?#]+)\/products\//);
    return match ? match[1] : '';
  }

  function withPrefix(next, prefix) {
    if (!prefix) return;
    next.querySelectorAll('a[href^="/products/"]').forEach(function (link) {
      link.setAttribute('href', prefix + link.getAttribute('href'));
    });
  }

  function applyCard(item, next) {
    withPrefix(next, collectionPrefix(item));

    var swatchRow = item.querySelector('.product-item__variant-info--static');
    var nextRow = next.querySelector('.product-item__variant-info--static');
    var text = item.querySelector('.product-item__text');
    var nextText = next.querySelector('.product-item__text');

    // Text block: everything but the swatch row comes from the sibling.
    if (text && nextText) {
      if (swatchRow && nextRow) nextRow.replaceWith(swatchRow);
      else if (swatchRow) nextText.prepend(swatchRow);
      text.replaceWith(nextText);
    }

    replace(item, next, '.product-item__image-link');
    replace(item, next, '.product-badges');

    // Buttons keep their listeners; only their data changes.
    var heart = item.querySelector('[data-wishlist-toggle]');
    syncAttributes(heart, next.querySelector('[data-wishlist-toggle]'));
    if (heart && window.BayaWishlist) {
      var saved = window.BayaWishlist.read().indexOf(heart.getAttribute('data-wishlist-handle')) !== -1;
      heart.classList.toggle('is-active', saved);
      heart.setAttribute('aria-pressed', saved ? 'true' : 'false');
    }
    syncAttributes(item.querySelector('[data-quick-shop-trigger]'), next.querySelector('[data-quick-shop-trigger]'));
    syncAttributes(
      item.querySelector('.product-item__product-form-variant-input'),
      next.querySelector('.product-item__product-form-variant-input')
    );

  }

  function setActive(item, swatch) {
    item.querySelectorAll('.product-item-swatch.is-active').forEach(function (el) {
      el.classList.remove('is-active');
      el.removeAttribute('aria-current');
    });
    if (!swatch) return;
    swatch.classList.add('is-active');
    swatch.setAttribute('aria-current', 'true');
  }

  function swap(swatch) {
    var item = swatch.closest('.product-item');
    var previous = item.querySelector('.product-item-swatch.is-active');

    // The ring moves on tap, before the network answers; the photo dims
    // (baya-mobile.css) until the colourway arrives, then fades back up.
    setActive(item, swatch);
    item.dataset.swapFor = swatch.href;
    item.setAttribute('aria-busy', 'true');
    item.classList.add('is-swapping');

    var isLatest = function () {
      return item.dataset.swapFor === swatch.href;
    };

    return fetchCard(swatch.href)
      .then(function (card) {
        var next = card.cloneNode(true);
        return preloadImage(next).then(function () {
          return next;
        });
      })
      .then(function (next) {
        // A later tap on another colour wins; drop this response.
        if (isLatest()) applyCard(item, next, swatch);
      })
      .catch(function () {
        if (!isLatest()) return;
        // Network or markup problem: put the ring back and use the plain link.
        setActive(item, previous);
        window.location.href = swatch.href;
      })
      .then(function () {
        if (!isLatest()) return;
        delete item.dataset.swapFor;
        item.removeAttribute('aria-busy');
        item.classList.remove('is-swapping');
      });
  }

  // Capture phase: the page-transition delegate on <body> (theme.js) would
  // otherwise take the link and leave the page before this runs.
  document.addEventListener(
    'click',
    function (event) {
      if (!mobile.matches) return;
      var swatch = event.target.closest(SWATCH);
      if (!swatch || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      // Second tap on the colour already shown opens the product.
      if (swatch.classList.contains('is-active')) return;
      event.preventDefault();
      event.stopPropagation();
      swap(swatch);
    },
    true
  );

  // Warm the cache as soon as a finger lands on a swatch.
  document.addEventListener(
    'pointerdown',
    function (event) {
      if (!mobile.matches) return;
      var swatch = event.target.closest(SWATCH);
      if (swatch && !swatch.classList.contains('is-active')) fetchCard(swatch.href).catch(function () {});
    },
    { passive: true }
  );
})();
